import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Spinner } from 'components/Elements/Spinner';

/**
 * Clean minimal Fundo-themed button
 */
const VARIANTS = {
  primary:
    'bg-fundo-green-600 hover:bg-fundo-green-700 text-white shadow-sm dark:bg-fundo-green-500 dark:hover:bg-fundo-green-600',
  secondary:
    'bg-white border border-gray-200 text-gray-800 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100',
  outline:
    'bg-transparent border border-fundo-green-600 text-fundo-green-700 hover:bg-fundo-green-50 dark:hover:bg-[rgba(16,185,129,0.06)]',
  ghost: 'bg-transparent text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
};

const SIZES = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm font-medium',
  lg: 'px-6 py-3 text-base font-semibold',
};

export const Button = React.forwardRef(
  (
    {
      type = 'button',
      variant = 'primary',
      size = 'md',
      className,
      isLoading = false,
      disabled = false,
      startIcon,
      endIcon,
      children,
      ...props
    },
    ref
  ) => {
    const disabledState = disabled || isLoading;
    return (
      <motion.button
        ref={ref}
        type={type}
        disabled={disabledState}
        whileTap={!disabledState ? { scale: 0.98 } : {}}
        className={clsx(
          'inline-flex items-center justify-center gap-2 rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-fundo-green-300 disabled:opacity-60 disabled:cursor-not-allowed',
          VARIANTS[variant],
          SIZES[size],
          className
        )}
        {...props}
      >
        {isLoading && <Spinner size="sm" className="text-current" />}

        {!isLoading && startIcon && <span className="flex items-center">{startIcon}</span>}

        <span className={clsx(isLoading && 'opacity-0')}>{children}</span>

        {!isLoading && endIcon && <span className="flex items-center">{endIcon}</span>}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  type: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'ghost', 'danger']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};
